import { css, SerializedStyles, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Styles } from '@silvertours/front-shared';
import { LayoutFlow, BackgroundStyle } from './rule';
import { SpacingDirection } from '../rule';

type ContainerProps = {
  isLarge?: boolean;
  layoutFlow?: LayoutFlow;
  backgroundStyle?: BackgroundStyle;
};

type WaveContainerProps = ContainerProps & {
  spacingDirection?: SpacingDirection;
};

const getLayoutFlow = (layoutFlow: LayoutFlow, theme: Theme) =>
  ({
    top: css`
      display: flex;
      flex-direction: column-reverse;
    `,
    bottom: css`
      display: flex;
      flex-direction: column;
    `,
    left: css`
      display: flex;
      flex-direction: column;

      ${theme.mq('lg')} {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, auto));
      }
    `,
    right: css`
      display: flex;
      flex-direction: column;

      ${theme.mq('lg')} {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, auto));
      }
    `,
  })[layoutFlow];

const getBackgroundColor = (
  theme: Theme,
  style: BackgroundStyle,
): SerializedStyles | string =>
  ({
    default: '',
    gray: css`
      background-color: ${theme.colors.neutral.grey20};
    `,
    green: '',
    greenAlt: '',
    mint: '',
    mintAlt: '',
    orange: '',
    orangeAlt: '',
    brand: '',
  })[style];

const getSpacingDirection = (
  spacingDirection: SpacingDirection,
  theme: Theme,
): SerializedStyles | string =>
  ({
    top: css`
      padding-top: 4rem;
      padding-bottom: 0;

      ${theme.mq('lg')} {
        padding-top: 6rem;
      }
    `,
    bottom: css`
      padding-top: 0;
      padding-bottom: 4rem;

      ${theme.mq('lg')} {
        padding-bottom: 6rem;
      }
    `,
    both: '',
    neither: '',
  })[spacingDirection];

const Container = styled.section<ContainerProps>`
  ${Styles.Global.layoutContainerContent}

  gap: 2.5rem;

  ${({ theme }) => theme.mq('lg')} {
    gap: 3rem;
  }

  ${({ theme, isLarge, layoutFlow }) =>
    isLarge && layoutFlow && getLayoutFlow(layoutFlow, theme)};

  ${({ theme, backgroundStyle }) =>
    backgroundStyle && getBackgroundColor(theme, backgroundStyle)};

  ${({ theme, isLarge, layoutFlow = '' }) =>
    isLarge &&
    !['left', 'right'].includes(layoutFlow) &&
    css`
      padding-top: 4rem;
      padding-bottom: 4rem;

      ${theme.mq('xl')} {
        padding-top: 6rem;
        padding-bottom: 6rem;
      }
    `};
`;

const WaveContainer = styled(Container)<WaveContainerProps>`
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;

  ${({ theme, spacingDirection }) =>
    spacingDirection && getSpacingDirection(spacingDirection, theme)};
`;

export { Container, WaveContainer };
